import React, { useState, Suspense, useRef, useEffect } from 'react';
import { ArrowRight, Phone, Image, MoreHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, getFullUrl } from '../config';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF, OrbitControls } from '@react-three/drei';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import ImageCarousel from './ImageCarousel';

// Interactive 3D Model Component for Item Details with loading callback
function InteractiveModel3D({ url, autoRotate = false, onLoad }) {
  const { scene } = useGLTF(url);
  const clonedScene = scene.clone();
  const groupRef = useRef();
  
  React.useEffect(() => {
    clonedScene.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
    
    // Trigger onLoad callback when model is ready
    if (onLoad) {
      onLoad();
    }
  }, [clonedScene, onLoad]);

  useFrame((state, delta) => {
    if (autoRotate && groupRef.current) {
      groupRef.current.rotation.y += delta * 0.5;
    }
  });

  return (
    <group ref={groupRef}>
      <primitive object={clonedScene} scale={1.2} />
    </group>
  );
}

// Simple Media Viewer with mobile-friendly responsive design and carousel support
function SimpleMediaViewer({ item }) {
  const { t } = useTranslation();
  const [modelLoaded, setModelLoaded] = useState(false);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Simple check: if URL ends with .glb or .gltf, it's a 3D model, otherwise it's an image
  const is3D = item.url && (item.url.endsWith('.glb') || item.url.endsWith('.gltf'));

  // Prepare all images for carousel
  const allImages = [];
  if (item.url && !is3D) {
    // For regular images, include main image first
    allImages.push(item.url);
  }
  // Always include additional images if they exist
  if (item.additionalImages && item.additionalImages.length > 0) {
    allImages.push(...item.additionalImages.map(img => img.url));
  }

  // For 3D objects, we have images if there are additional images
  // For regular images, we have multiple images if more than 1 total
  const hasMultipleImages = is3D
    ? (item.additionalImages && item.additionalImages.length > 0)
    : allImages.length > 1;

  // Shared responsive height classes - full width on mobile, larger height
  const containerClasses = "w-full h-[400px] sm:h-[500px] lg:h-[600px] lg:rounded lg:border overflow-hidden relative";

  if (is3D) {
    // For 3D objects, always show clickable thumbnail and open carousel
    const thumbnailUrl = item.thumbnailUrl || item.url; // Fallback to model URL if no thumbnail
    const showThumbnail = item.thumbnailUrl; // Only show thumbnail image if thumbnailUrl exists

    return (
      <>
        <div
          className={`${containerClasses} bg-gray-50 cursor-pointer`}
          onClick={() => {
            setCarouselIndex(0);
            setCarouselOpen(true);
          }}
        >
          {showThumbnail ? (
            <img
              src={getFullUrl(thumbnailUrl)}
              alt={item.name}
              className="w-full h-full object-contain"
              draggable={false}
            />
          ) : (
            // Fallback to 3D icon if no thumbnail
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-gray-400 text-6xl">🏺</div>
            </div>
          )}

          {/* Always show indicator for 3D objects (they can be viewed in carousel) */}
          <div className="absolute top-4 right-4 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm flex items-center gap-1">
            <span className="text-xs">3D</span>
            {hasMultipleImages && (
              <>
                <span>+</span>
                <Image className="w-4 h-4" />
                <span>{item.additionalImages.length}</span>
              </>
            )}
          </div>
        </div>

        {/* 3D + Images Carousel */}
        <ImageCarousel
          images={allImages}
          isOpen={carouselOpen}
          onClose={() => setCarouselOpen(false)}
          initialIndex={carouselIndex}
          item={item} // Pass the item for 3D rendering in carousel
          is3D={true}
        />
      </>
    );
  } 
  
  // Show as image if it's not a 3D model
  if (item.url && !is3D) {
    return (
      <>
        <div
          className={`${containerClasses} bg-gray-50 cursor-pointer`}
          onClick={() => {
            setCarouselIndex(0);
            setCarouselOpen(true);
          }}
        >
          <img
            src={getFullUrl(item.url)}
            alt={item.name}
            className="w-full h-full object-contain"
            draggable={false}
          />

          {/* Additional images indicator */}
          {hasMultipleImages && (
            <div className="absolute top-4 right-4 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm flex items-center gap-1">
              <Image className="w-4 h-4" />
              <span>{allImages.length}</span>
            </div>
          )}

        </div>

        {/* Image Carousel */}
        <ImageCarousel
          images={allImages}
          isOpen={carouselOpen}
          onClose={() => setCarouselOpen(false)}
          initialIndex={carouselIndex}
        />
      </>
    );
  }

  return (
    <div className={`${containerClasses} bg-gray-100 flex items-center justify-center`}>
      <div className="text-gray-400 text-6xl">🎨</div>
    </div>
  );
}

function ItemDetailsView({ data, onTeleportToItem, onShowConfirmation, currentShowroomId }) {
  const { t } = useTranslation();
  const { item, showrooms } = data;
  
  // Simple check: if URL ends with .glb or .gltf, it's a 3D model, otherwise it's an image
  const is3D = item.url && (item.url.endsWith('.glb') || item.url.endsWith('.gltf'));
  
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestData, setRequestData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [requestStatus, setRequestStatus] = useState(null);

  const handleTeleportToItem = () => {
    if (showrooms.length === 0) {
      alert(t('artworkNotDisplayed'));
      return;
    }

    const currentRoomPlacement = showrooms.find(s => s.id === currentShowroomId);

    if (currentRoomPlacement) {
      onTeleportToItem(item, currentRoomPlacement.placement);
    } else {
      const targetShowroom = showrooms[0];
      onShowConfirmation(
        `${t('teleportToItem')} "${item.name}"?`,
        `${item.name} ${t('itemLocation')} "${targetShowroom.name}". ${t('switchRooms')}`,
        () => onTeleportToItem(item, targetShowroom.placement, targetShowroom.id)
      );
    }
  };

  const handleCleverSiteRequest = () => {
    // Create the message with item information
    const message = `Я бы хотел узнать подробнее о:
ID: ${item.id}
Название: ${item.name}`;

    // Open the chat widget
    if (window.cleversiteEvent) {
      window.cleversiteEvent.trigger('openWidgetChat');
    }

    // Wait for chat to open, then set message and send
    setTimeout(() => {
      const chatInput = document.querySelector('.clv_input_message');
      if (chatInput) {
        chatInput.textContent = message;

        setTimeout(() => {
          const sendButton = document.querySelector('.cleversite_clever_button-send');
          if (sendButton) {
            sendButton.click();

            // Clear the input after sending
            setTimeout(() => {
              chatInput.textContent = '';
            }, 100);
          }
        }, 100);
      }
    }, 500);
  };

  const handleRequestInfo = async (e) => {
    e.preventDefault();
    
    if (!requestData.name.trim() || !requestData.email.trim() || !requestData.phone.trim()) {
      alert(t('fillRequiredFields'));
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/callback-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...requestData,
          type: 'catalog',
          message: `Information request for item: ${item.name}\n\n${requestData.message || 'No additional message.'}`
        })
      });

      if (response.ok) {
        setRequestStatus('success');
        setRequestData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => {
          setShowRequestForm(false);
          setRequestStatus(null);
        }, 2000);
      } else {
        setRequestStatus('error');
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      setRequestStatus('error');
    }
  };

  if (showRequestForm) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('requestInformation')}</h3>
          <p className="text-sm text-gray-600">{t('requestInfoFor')} "{item.name}"</p>
        </div>

        {requestStatus === 'success' ? (
          <div className="text-center py-8">
            <div className="text-green-600 mb-2">✓ {t('requestSubmitted')}</div>
            <p className="text-sm text-gray-600">{t('contactSoon')}</p>
          </div>
        ) : (
          <form onSubmit={handleRequestInfo} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('name')} *</label>
              <input
                type="text"
                value={requestData.name}
                onChange={(e) => setRequestData({...requestData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('email')} *</label>
              <input
                type="email"
                value={requestData.email}
                onChange={(e) => setRequestData({...requestData, email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('phone')} *</label>
              <input
                type="tel"
                value={requestData.phone}
                onChange={(e) => setRequestData({...requestData, phone: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('message')}</label>
              <textarea
                value={requestData.message}
                onChange={(e) => setRequestData({...requestData, message: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                placeholder={t('anySpecificQuestions')}
              />
            </div>

            {requestStatus === 'error' && (
              <div className="text-red-600 text-sm">{t('submissionFailed')}</div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowRequestForm(false)}
                className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded text-sm hover:bg-gray-50"
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-gray-900 text-white rounded text-sm hover:bg-black"
              >
                {t('submit')} {t('requestInformation')}
              </button>
            </div>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="lg:p-6">
      {/* Mobile: Full width media viewer first */}
      <div className="lg:hidden">
        <SimpleMediaViewer item={item} />
      </div>

      {/* Mobile: Details section with padding */}
      <div className="p-6 lg:hidden">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-2xl font-medium text-gray-900">{item.name}</h3>
            <span className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded">
              {is3D ? t('threeObject') : t('painting')}
            </span>
          </div>

          <p className="text-gray-600 mb-6 leading-relaxed">{item.description}</p>

          <div className="text-sm text-gray-500 mb-6">
            <div><strong>{t('dimensions')}:</strong> {item.width} × {item.height} × {item.depth}</div>
          </div>
        </div>

        {/* Location Info */}
        {showrooms.length > 0 && (
          <div className="mb-8">
            <h4 className="font-medium text-gray-900 mb-3">{t('currentExhibition')}</h4>
            <div className="space-y-2">
              {showrooms.map((showroom) => (
                <div key={showroom.id} className="p-3 bg-gray-50 rounded border">
                  <div className="font-medium text-gray-900">
                    {showroom.name}
                    {showroom.id === currentShowroomId && (
                      <span className="ml-2 text-xs text-gray-500">({t('yourCurrentLocation')})</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3" style={{ position: 'relative', zIndex: 10 }}>
          {showrooms.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleTeleportToItem();
              }}
              className="w-full flex items-center justify-center px-4 py-3 bg-gray-900 text-white font-medium rounded hover:bg-black transition-colors"
              style={{
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation',
                cursor: 'pointer',
                position: 'relative',
                zIndex: 1
              }}
            >
              <span style={{ pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
                {t('teleportToItem')}
                <ArrowRight style={{ width: '16px', height: '16px', marginLeft: '8px' }} />
              </span>
            </button>
          )}

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCleverSiteRequest();
            }}
            className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded hover:bg-gray-50 transition-colors"
            style={{
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
              cursor: 'pointer',
              position: 'relative',
              zIndex: 1
            }}
          >
            <span style={{ pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
              <Phone style={{ width: '16px', height: '16px', marginRight: '8px' }} />
              {t('requestInformation')}
            </span>
          </button>
        </div>
      </div>

      {/* Desktop: 2-column layout */}
      <div className="hidden lg:grid lg:grid-cols-2 gap-8">
        {/* Left Column - Media Viewer */}
        <div>
          <SimpleMediaViewer item={item} />
        </div>

        {/* Right Column - Details */}
        <div>
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-2xl font-medium text-gray-900">{item.name}</h3>
              <span className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded">
                {is3D ? t('threeObject') : t('painting')}
              </span>
            </div>
            
            <p className="text-gray-600 mb-6 leading-relaxed">{item.description}</p>
            
            <div className="text-sm text-gray-500 mb-6">
              <div><strong>{t('dimensions')}:</strong> {item.width} × {item.height} × {item.depth}</div>
            </div>
          </div>

          {/* Location Info */}
          {showrooms.length > 0 && (
            <div className="mb-8">
              <h4 className="font-medium text-gray-900 mb-3">{t('currentExhibition')}</h4>
              <div className="space-y-2">
                {showrooms.map((showroom) => (
                  <div key={showroom.id} className="p-3 bg-gray-50 rounded border">
                    <div className="font-medium text-gray-900">
                      {showroom.name}
                      {showroom.id === currentShowroomId && (
                        <span className="ml-2 text-xs text-gray-500">({t('yourCurrentLocation')})</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3" style={{ position: 'relative', zIndex: 10 }}>
            {showrooms.length > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleTeleportToItem();
                }}
                className="w-full flex items-center justify-center px-4 py-3 bg-gray-900 text-white font-medium rounded hover:bg-black transition-colors"
                style={{
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation',
                  cursor: 'pointer',
                  position: 'relative',
                  zIndex: 1
                }}
              >
                <span style={{ pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
                  {t('teleportToItem')}
                  <ArrowRight style={{ width: '16px', height: '16px', marginLeft: '8px' }} />
                </span>
              </button>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCleverSiteRequest();
              }}
              className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded hover:bg-gray-50 transition-colors"
              style={{
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation',
                cursor: 'pointer',
                position: 'relative',
                zIndex: 1
              }}
            >
              <span style={{ pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
                <Phone style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                {t('requestInformation')}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ItemDetailsView;